// src/pages/Handover.tsx — Handover document register, DLP tracking, certificate generation
import { useState, useMemo } from 'react'
import { useAuthStore } from '../stores/auth.store'
import { MOCK_HANDOVER_RECORDS, HANDOVER_DOC_STATUS } from '../utils/mockDataTier5'
import { useQuery } from '@tanstack/react-query'
import { api } from '../utils/api'
import { fetchHandoverPacks } from '../services/handoverService'
import { MOCK_CONTRACTS } from '../utils/mockDataCommercial'
import { MOCK_SUBCONTRACTS } from '../utils/mockDataSubcontracts'
import { formatCurrency, formatDate } from '../utils/formatters'
import { MOCK_CUSTODY_RECORDS, CUSTODY_EVENT_META } from '../utils/mockDataMobile'
import { computeHandoverChecklist, type HandoverChecklistItem } from '../utils/workflowPipeline'
import { autoFileHandoverToArchive, getHandoverReadyFlag } from '../utils/workflowEvents'
import {
  FileText, CheckCircle, Clock, AlertTriangle,
  ChevronRight, Download, Plus, ArrowLeft, Zap, Info, Package, ArrowRight, DollarSign, Building,
} from 'lucide-react'

type HandoverRecord = typeof MOCK_HANDOVER_RECORDS[0]
type HandoverDoc    = HandoverRecord['documents'][0]

const HO_STATUS: Record<string, { label: string; bg: string; text: string; dot: string }> = {
  NOT_STARTED:  { label: 'Not started',  bg: 'bg-gray-100',  text: 'text-gray-600',  dot: 'bg-gray-400' },
  IN_PROGRESS:  { label: 'In progress',  bg: 'bg-blue-100',  text: 'text-blue-700',  dot: 'bg-blue-500' },
  READY:        { label: 'Ready',        bg: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-500' },
  HANDED_OVER:  { label: 'Handed over',  bg: 'bg-purple-100',text: 'text-purple-700',dot: 'bg-purple-500' },
}

// Payment Clearance Schedule PDF — BIF Act 2017 (Qld) — final handover evidence
function generatePaymentClearanceSchedule(record: any, subcontracts: typeof MOCK_SUBCONTRACTS) {
  const now    = new Date().toLocaleDateString('en-AU', { day:'numeric', month:'long', year:'numeric' })
  const fmtD   = (iso?: string) => iso ? new Date(iso).toLocaleDateString('en-AU', { day:'numeric', month:'short', year:'numeric' }) : '—'
  const fmtAUD = (n: number) => new Intl.NumberFormat('en-AU', { style:'currency', currency:'AUD', minimumFractionDigits:0 }).format(n)

  const jobSubs = subcontracts.filter(sc => sc.jobId === record.jobId || sc.jobRef === record.jobNumber)
  const rows    = jobSubs.flatMap(sc =>
    sc.milestones.map(ms => ({ ...ms, subName: sc.subcontractorName, subABN: sc.subcontractorABN, ref: sc.reference }))
  )
  const totalPayable = rows.reduce((s, r) => s + r.amountPayable, 0)
  const totalPaid    = rows.filter(r => r.paid).reduce((s, r) => s + r.amountPayable, 0)
  const unpaid       = rows.filter(r => !r.paid)
  const allPaid      = unpaid.length === 0

  const rowHtml = rows.map((r, i) => `
    <tr style="background:${i%2===0?'#fff':'#fafafa'}">
      <td style="padding:6px 10px">${r.subName}<br><span style="font-size:8pt;color:#888">ABN ${r.subABN} · ${r.ref}</span></td>
      <td style="padding:6px 8px;font-size:9pt">${r.description.slice(0,55)}</td>
      <td style="padding:6px 8px;font-family:monospace;text-align:right">${fmtAUD(r.amountPayable)}</td>
      <td style="padding:6px 8px;text-align:center;font-size:9pt">${r.invoiceRef ?? '—'}</td>
      <td style="padding:6px 8px;text-align:center;font-weight:bold;color:${r.paid?'#166534':'#b91c1c'}">${r.paid?`✓ ${r.paidAt?fmtD(r.paidAt):'Paid'}`:'UNPAID'}</td>
    </tr>`).join('')

  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>Payment Clearance Schedule — ${record.jobNumber}</title>
<style>*{box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:10.5pt;color:#111;margin:18mm}
h1{font-size:20pt;font-weight:900;color:#1B3A6B;margin:0}
.hdr{border-bottom:3px solid #1B3A6B;padding-bottom:10px;margin-bottom:16px;display:flex;justify-content:space-between;align-items:flex-end}
.meta{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:14px}
.mb{border:1px solid #e5e7eb;border-radius:4px;padding:7px 12px}.mb .l{font-size:8pt;color:#9ca3af;margin-bottom:1px}.mb .v{font-weight:600;font-size:9.5pt}
table{width:100%;border-collapse:collapse;margin-bottom:12px}
th{background:#1B3A6B;color:#fff;padding:6px 10px;text-align:left;font-size:9.5pt}
.sum{display:flex;gap:10px;margin-bottom:14px}
.sum-box{flex:1;border:1px solid #e5e7eb;border-radius:5px;padding:8px;text-align:center}
.sum-v{font-size:16pt;font-weight:900}
.ok{background:#f0fdf4;border:2px solid #86efac;border-radius:5px;padding:10px 14px;color:#166534;font-weight:bold;margin-bottom:12px}
.warn{background:#fee2e2;border:2px solid #fca5a5;border-radius:5px;padding:10px 14px;color:#991b1b;font-weight:bold;margin-bottom:12px}
.decl{background:#fef3c7;border:1.5px solid #d97706;border-radius:5px;padding:12px 16px;line-height:1.8;margin-bottom:14px}
.sig-row{display:flex;gap:30px;margin-top:20px}
.sig-block{flex:1}.sig-line{border-bottom:1.5px solid #333;height:32px;margin-bottom:4px}.sig-lbl{font-size:8pt;color:#888}
.footer{font-size:7.5pt;color:#aaa;text-align:center;margin-top:16px;border-top:1px solid #eee;padding-top:6px}
@media print{body{margin:14mm}}</style></head><body>
<div class="hdr">
  <div><h1>MCB Civil Electrical Pty Ltd</h1><div style="font-size:9pt;color:#888">ABN 44 128 993 087 · 14 Commerce Dr, Salisbury QLD 4107</div></div>
  <div style="text-align:right"><div style="font-size:13pt;font-weight:900;color:#1B3A6B">PAYMENT CLEARANCE SCHEDULE</div>
  <div style="font-size:9pt;color:#888">BIF Act 2017 (Qld) · Handover Evidence Document</div><div style="font-size:9pt;color:#888">${now}</div></div>
</div>
<div class="meta">
  <div class="mb"><div class="l">Job / project</div><div class="v">${record.jobNumber}</div></div>
  <div class="mb"><div class="l">Description</div><div class="v">${record.description}</div></div>
  <div class="mb"><div class="l">Client</div><div class="v">${record.clientName}</div></div>
  <div class="mb"><div class="l">Date prepared</div><div class="v">${now}</div></div>
</div>
${allPaid
  ? `<div class="ok">✓ ALL PAYMENTS CLEARED — All subcontractor and supplier payments for this project have been confirmed paid in full. This schedule may be submitted with the handover documentation.</div>`
  : `<div class="warn">⚠ ${unpaid.length} PAYMENT${unpaid.length!==1?'S':''} OUTSTANDING — The following amounts remain unpaid. These must be settled before the payment clearance schedule can be signed and included in the handover pack.</div>`}
<div class="sum">
  <div class="sum-box"><div class="sum-v" style="color:#1B3A6B">${rows.length}</div><div style="font-size:9pt;color:#6b7280">Milestones</div></div>
  <div class="sum-box"><div class="sum-v" style="color:#166534">${rows.filter(r=>r.paid).length}</div><div style="font-size:9pt;color:#6b7280">Paid</div></div>
  <div class="sum-box"><div class="sum-v" style="color:${unpaid.length>0?'#b91c1c':'#166534'}">${unpaid.length}</div><div style="font-size:9pt;color:#6b7280">Outstanding</div></div>
  <div class="sum-box"><div class="sum-v" style="color:#1B3A6B;font-size:13pt">${fmtAUD(totalPaid)}</div><div style="font-size:9pt;color:#6b7280">Total paid</div></div>
  <div class="sum-box"><div class="sum-v" style="color:#1B3A6B;font-size:13pt">${fmtAUD(totalPayable)}</div><div style="font-size:9pt;color:#6b7280">Total payable</div></div>
</div>
<h2 style="font-size:11pt;color:#1B3A6B;border-bottom:1.5px solid #1B3A6B;padding-bottom:3px;margin:0 0 8px">Subcontractor &amp; Supplier Payment Schedule</h2>
<table><thead><tr>
  <th style="width:28%">Subcontractor / ABN</th><th style="width:28%">Milestone</th>
  <th style="width:15%;text-align:right">Amount paid</th><th style="width:15%;text-align:center">Invoice ref</th>
  <th style="width:14%;text-align:center">Payment status</th>
</tr></thead><tbody>${rowHtml}</tbody>
<tfoot><tr style="background:#1B3A6B;color:#fff;font-weight:bold">
  <td colspan="2" style="padding:7px 10px">TOTAL</td>
  <td style="padding:7px 8px;text-align:right;font-family:monospace">${fmtAUD(totalPayable)}</td>
  <td colspan="2"></td></tr></tfoot></table>
<div class="decl">
  <p><strong>I, _________________________, Project Manager, MCB Civil Electrical Pty Ltd</strong>, declare that the subcontractor and supplier payment schedule above is true and correct, and that all amounts payable to subcontractors and suppliers for work performed on the above project have been paid in full, except as otherwise noted above.</p>
  <p style="margin-top:8px">This declaration is made in connection with the handover of the above project and is provided as evidence of payment compliance under the Building Industry Fairness (Security of Payment) Act 2017 (Qld).</p>
</div>
<div class="sig-row">
  <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">Project Manager — MCB Civil Electrical Pty Ltd · name &amp; date</div></div>
  <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">Client representative — acknowledged &amp; date</div></div>
</div>
<div class="footer">MCB Civil Electrical Pty Ltd · Payment Clearance Schedule · ${record.jobNumber} · ${now}<br>
BIF Act 2017 (Qld) · Retain with handover and project financial records.</div>
</body></html>`

  const w = window.open('', '_blank', 'width=1020,height=820')
  if (!w) { alert('Pop-up blocked'); return }
  w.document.write(html); w.document.close()
  setTimeout(() => { w.focus(); w.print() }, 350)
}

// Handover detail
function HandoverDetail({ record: initial, onBack }: { record: HandoverRecord; onBack: () => void }) {
  const { isTier } = useAuthStore()
  const [record, setRecord] = useState(initial)
  const [activeTab, setActiveTab] = useState<'documents'|'computed'|'dlp'|'custody'|'payment-clearance'>('documents')
  const [showComputed, setShowComputed] = useState(true)
  const [archiveFiled, setArchiveFiled] = useState(false)
  const [archiveRef, setArchiveRef]     = useState<string | null>(null)

  // Phase 3: compute real status from system modules
  const computedChecklist = useMemo(() => computeHandoverChecklist(record.jobId), [record.jobId])

  const contract = MOCK_CONTRACTS.find(c => c.jobId === record.jobId)
  const complete  = record.documents.filter(d => d.status === 'COMPLETE').length
  const total     = record.documents.filter(d => d.required).length
  const pct       = total > 0 ? Math.round(complete / total * 100) : 0
  const allReady  = pct === 100

  // Find computed status for a document by matching its name to the checklist
  const getComputedStatus = (doc: HandoverDoc): HandoverChecklistItem | null => {
    if (!showComputed) return null
    return computedChecklist.find(c =>
      c.isComputed && (
        doc.name.toLowerCase().includes('itp') && c.name.toLowerCase().includes('itp') ||
        doc.name.toLowerCase().includes('hold point') && c.name.toLowerCase().includes('hold point') ||
        doc.name.toLowerCase().includes('work instruction') && c.name.toLowerCase().includes('work instruction') ||
        doc.name.toLowerCase().includes('bom') && c.name.toLowerCase().includes('bom') ||
        doc.name.toLowerCase().includes('compli') && c.name.toLowerCase().includes('compli') ||
        doc.name.toLowerCase().includes('material') && c.name.toLowerCase().includes('material')
      )
    ) ?? null
  }

  const updateDocStatus = (docId: string, status: string) => {
    setRecord(r => ({ ...r, documents: r.documents.map(d => d.id === docId ? { ...d, status: status as any } : d) }))
  }

  const daysToTarget = record.targetHandoverDate
    ? Math.ceil((new Date(record.targetHandoverDate).getTime() - new Date('2026-06-17').getTime()) / 86400000)
    : null

  // Computed readiness score
  const computedComplete = computedChecklist.filter(c => c.status === 'COMPLETE').length
  const computedTotal    = computedChecklist.length
  const computedPct      = Math.round((computedComplete / computedTotal) * 100)

  return (
    <div>
      <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 mb-4">
        <ArrowLeft className="w-4 h-4" /> Handover register
      </button>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-5">
        <div className="px-6 py-5 border-b">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="font-bold text-gray-900 text-lg">{record.jobNumber}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${HO_STATUS[record.status]?.bg ?? 'bg-gray-100'} ${HO_STATUS[record.status]?.text ?? 'text-gray-600'}`}>
                  {HO_STATUS[record.status]?.label ?? record.status}
                </span>
                {daysToTarget !== null && daysToTarget <= 21 && (
                  <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${daysToTarget <= 7 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                    {daysToTarget > 0 ? `${daysToTarget}d to handover` : 'Handover date passed'}
                  </span>
                )}
              </div>
              <p className="text-base font-medium text-gray-700">{record.description}</p>
              <p className="text-xs text-gray-400 mt-0.5">{record.clientName} · Target handover: {record.targetHandoverDate ? formatDate(record.targetHandoverDate) : 'TBC'}</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => generateForm16(record)} className="btn-secondary flex items-center gap-1.5 text-sm">
                <Download className="w-3.5 h-3.5" />Form 16
              </button>
              <button onClick={() => generatePaymentClearanceSchedule(record, MOCK_SUBCONTRACTS)} className="btn-secondary flex items-center gap-1.5 text-sm">
                <DollarSign className="w-3.5 h-3.5" />Payment clearance
              </button>
              <button onClick={() => generatePracticalCompletionCertificate(record, contract)} className="btn-secondary flex items-center gap-1.5 text-sm">
                <Download className="w-3.5 h-3.5" />Practical completion cert
              </button>
              {allReady && isTier('B_PM') && (
                <button
                  onClick={() => {
                    generateHandoverPackPDF(record, contract)
                    // P3-4: Auto-file to Archive Centre
                    const filed = autoFileHandoverToArchive(
                      { jobId: record.jobId, jobNumber: record.jobNumber, description: record.description, clientName: record.clientName },
                      'PM'
                    )
                    setArchiveRef(filed.packRef)
                    setArchiveFiled(true)
                  }}
                  className="btn-primary flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />Generate handover pack
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-3 divide-x text-sm border-b">
          {[
            { label: 'Required documents', value: `${complete} / ${total} complete` },
            { label: 'Readiness', value: `${pct}%`, bold: true },
            { label: 'DLP period', value: `${record.dlpMonths} months from handover` },
          ].map(f => (
            <div key={f.label} className="px-4 py-3">
              <div className="text-xs text-gray-500">{f.label}</div>
              <div className={`mt-0.5 ${(f as any).bold ? 'font-bold text-base text-brand-blue' : 'font-medium'}`}>{f.value}</div>
            </div>
          ))}
        </div>
        <div className="h-2 bg-gray-100">
          <div className={`h-full transition-all ${pct === 100 ? 'bg-green-500' : 'bg-brand-blue'}`} style={{ width: `${pct}%` }} />
        </div>
      </div>

      {/* P3-3: ITP handover readiness flag */}
      {getHandoverReadyFlag(record.jobId) && (
        <div className="flex items-center gap-2 bg-teal-50 border border-teal-300 rounded-xl px-4 py-3 text-sm text-teal-800">
          <CheckCircle className="w-4 h-4 text-teal-600 flex-shrink-0" />
          <span><strong>ITP complete</strong> — all hold points released for {record.jobNumber}. This job is ready for handover.</span>
        </div>
      )}

      {/* P3-4: Archive filing confirmation */}
      {archiveFiled && archiveRef && (
        <div className="flex items-center gap-3 bg-green-50 border border-green-300 rounded-xl px-4 py-3 text-sm text-green-800">
          <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
          <div>
            <span className="font-semibold">Handover pack auto-filed to Archive Centre.</span>
            {' '}Reference: <code className="bg-green-100 px-1 rounded text-xs font-mono">{archiveRef}</code>.
            {' '}Go to Archive Centre → Documents → {record.jobNumber} to view.
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-5 bg-gray-100 rounded-xl p-1 w-fit">
        {([{key:'documents', label:'Document checklist'},{key:'computed', label:`System checklist (${computedPct}%)`},{key:'dlp', label:'Defects liability'},{key:'custody', label:`Chain of custody${MOCK_CUSTODY_RECORDS.filter(c=>c.jobId===record.jobId).length > 0 ? ` (${MOCK_CUSTODY_RECORDS.filter(c=>c.jobId===record.jobId).length})` : ''}`},{key:'payment-clearance', label:'Payment clearance'}] as const).map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key as any)}
            className={`text-sm font-medium px-5 py-2 rounded-lg transition-colors ${activeTab === t.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── System checklist tab — Phase 3 ─── */}
      {(activeTab as string) === 'computed' && (
        <div className="space-y-3">
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 flex items-start gap-2 text-xs text-indigo-800">
            <Zap className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <div>
              <strong>System-computed checklist</strong> — these items are derived live from Work Instructions, ITP hold point records, and the BOM compliance store. They update automatically when field records change. Manual-only items (as-builts, Form 16 reference, DLP confirmation) are shown with an edit icon.
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="px-5 py-3 bg-gray-50 border-b flex items-center justify-between">
              <span className="text-xs text-gray-600 font-medium">
                {computedComplete} of {computedTotal} checklist items complete ({computedPct}%)
              </span>
              <div className="h-2 w-32 bg-gray-200 rounded-full overflow-hidden">
                <div className={`h-full transition-all ${computedPct === 100 ? 'bg-green-500' : 'bg-indigo-500'}`} style={{ width: `${computedPct}%` }} />
              </div>
            </div>
            <div className="divide-y divide-gray-100">
              {computedChecklist.map((item, i) => {
                const statusColors = {
                  COMPLETE: 'bg-green-500', IN_PROGRESS: 'bg-blue-400',
                  NOT_STARTED: 'bg-gray-300', BLOCKED: 'bg-red-400',
                }
                const textColors = {
                  COMPLETE: 'text-green-700 bg-green-50', IN_PROGRESS: 'text-blue-700 bg-blue-50',
                  NOT_STARTED: 'text-gray-500 bg-gray-50', BLOCKED: 'text-red-700 bg-red-50 border border-red-200',
                }
                return (
                  <div key={i} className={`flex items-start gap-3 px-5 py-3.5 ${item.status === 'BLOCKED' ? 'bg-red-50/30' : ''}`}>
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${statusColors[item.status as keyof typeof statusColors] ?? 'bg-gray-300'}`}>
                      {item.status === 'COMPLETE' && <CheckCircle className="w-3 h-3 text-white" />}
                      {item.status === 'BLOCKED' && <AlertTriangle className="w-3 h-3 text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-gray-900">{item.name}</span>
                        {item.isComputed
                          ? <span className="text-[10px] bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded-full font-medium">⚡ System-derived</span>
                          : <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">✏ Manual</span>}
                      </div>
                      <p className={`text-xs mt-1 px-2 py-1 rounded-lg font-medium ${textColors[item.status as keyof typeof textColors] ?? 'text-gray-500 bg-gray-50'}`}>
                        {item.computedNote}
                      </p>
                      {item.blockedBy && (
                        <p className="text-xs text-red-600 mt-0.5">Blocked by: {item.blockedBy}</p>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'documents' && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b flex items-center justify-between bg-gray-50">
            <div className="text-xs text-gray-500">Required: {total} · Complete: {complete} · {record.documents.filter(d => !d.required).length} optional</div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1.5 text-xs text-indigo-600 cursor-pointer">
                <input type="checkbox" checked={showComputed} onChange={e => setShowComputed(e.target.checked)} className="w-3 h-3" />
                <Zap className="w-3 h-3" /> Show system status
              </label>
              {isTier('B_PM') && <button className="text-xs text-blue-600 hover:underline">+ Add document</button>}
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {record.documents.map(doc => {
              const ds = HANDOVER_DOC_STATUS[doc.status] ?? HANDOVER_DOC_STATUS.NOT_STARTED
              const today = new Date('2026-06-17')
              const overdue = doc.dueDate && new Date(doc.dueDate) < today && doc.status !== 'COMPLETE'
              const computed = getComputedStatus(doc)
              return (
                <div key={doc.id} className={`flex items-start gap-3 px-5 py-3.5 ${overdue ? 'bg-red-50' : ''}`}>
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${computed?.status === 'COMPLETE' ? 'bg-green-500' : doc.status === 'COMPLETE' ? 'bg-green-500' : 'bg-gray-200'}`}>
                    {(doc.status === 'COMPLETE' || computed?.status === 'COMPLETE') && <CheckCircle className="w-3 h-3 text-white" />}
                    {computed?.status === 'BLOCKED' && doc.status !== 'COMPLETE' && <AlertTriangle className="w-3 h-3 text-amber-600" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-2">
                      <span className="text-sm font-medium text-gray-900 flex-1">{doc.name}</span>
                      {!doc.required && <span className="text-xs text-gray-400 flex-shrink-0">Optional</span>}
                      {overdue && <span className="text-xs text-red-600 font-bold flex-shrink-0">Overdue</span>}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${ds.bg} ${ds.text}`}>{ds.label}</span>
                      {doc.dueDate && <span className="text-xs text-gray-400">Due {formatDate(doc.dueDate)}</span>}
                      {computed && (
                        <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium flex items-center gap-1 ${computed.status === 'COMPLETE' ? 'bg-green-100 text-green-700' : computed.status === 'BLOCKED' ? 'bg-red-100 text-red-700' : 'bg-indigo-100 text-indigo-700'}`}>
                          <Zap className="w-2.5 h-2.5" />{computed.computedNote}
                        </span>
                      )}
                    </div>
                    {doc.notes && <p className="text-xs text-gray-500 mt-0.5 italic">{doc.notes}</p>}
                  </div>
                  {isTier('B_PM') && doc.status !== 'COMPLETE' && (
                    <select className="text-xs border border-gray-200 rounded px-1.5 py-1 flex-shrink-0"
                      value={doc.status}
                      onChange={e => updateDocStatus(doc.id, e.target.value)}>
                      <option value="NOT_STARTED">Not started</option>
                      <option value="IN_PROGRESS">In progress</option>
                      <option value="COMPLETE">Complete</option>
                      <option value="NOT_REQUIRED">N/A</option>
                    </select>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {activeTab === 'dlp' && (
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h3 className="font-semibold text-gray-900 mb-4">Defects Liability Period</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><span className="text-gray-500">DLP duration</span><div className="font-bold mt-0.5">{record.dlpMonths} months</div></div>
              <div><span className="text-gray-500">Commences</span><div className="font-medium mt-0.5">{record.actualHandoverDate ? formatDate(record.actualHandoverDate) : 'On practical completion'}</div></div>
              <div><span className="text-gray-500">Expires</span><div className="font-medium mt-0.5">{record.dlpEndDate ? formatDate(record.dlpEndDate) : '—'}</div></div>
              <div><span className="text-gray-500">Retention held</span><div className="font-medium mt-0.5">{contract ? formatCurrency(contract.retentionHeld) : '—'}</div></div>
            </div>
            <div className="mt-5 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <p className="font-semibold mb-1">Retention release reminder</p>
              <p>Retention is typically held in two stages: 50% on practical completion and 50% on DLP expiry. Submit a retention release claim to the superintendent at the end of DLP. Do not wait for the client to release it automatically.</p>
            </div>
          </div>

          {contract && (
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h3 className="font-semibold text-gray-900 mb-3">Contract reference</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-gray-500">Contract number</span><div className="font-medium mt-0.5">{contract.contractNumber}</div></div>
                <div><span className="text-gray-500">Type</span><div className="font-medium mt-0.5">{contract.contractType}</div></div>
                <div><span className="text-gray-500">PC date</span><div className="font-medium mt-0.5">{formatDate(contract.practicalCompletionDate)}</div></div>
                <div><span className="text-gray-500">LD rate</span><div className="font-medium mt-0.5">{formatCurrency(contract.liquidatedDamagesRate)}/day</div></div>
              </div>
            </div>
          )}
        </div>
      )}

      {(activeTab as string) === 'custody' && (
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-xl px-5 py-4">
            <h3 className="font-semibold text-gray-900 mb-1">Materials chain of custody</h3>
            <p className="text-sm text-gray-500">
              All material custody events logged by field crew for this job.
              Each transition (collected → transported → installed) is signed off and
              timestamped for inclusion in the handover documentation pack.
            </p>
          </div>

          {(() => {
            const jobCustody = MOCK_CUSTODY_RECORDS.filter(c => c.jobId === record.jobId)
            if (jobCustody.length === 0) {
              return (
                <div className="bg-white border border-gray-200 rounded-xl py-14 text-center text-gray-400">
                  <Package className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">No custody records for this job yet</p>
                  <p className="text-xs text-gray-300 mt-1">
                    Custody events are logged by field crew in Mobile Comms → Deliveries
                  </p>
                </div>
              )
            }
            const byBom = jobCustody.reduce((acc, c) => {
              if (!acc[c.bomLineId]) acc[c.bomLineId] = []
              acc[c.bomLineId].push(c)
              return acc
            }, {} as Record<string, typeof jobCustody>)
            return (
              <div className="space-y-3">
                <div className="flex justify-end">
                  <button className="btn-secondary text-sm flex items-center gap-2">
                    <Download className="w-4 h-4" />Include in handover pack
                  </button>
                </div>
                {Object.entries(byBom).map(([bomLineId, events]) => {
                  const sorted = [...events].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
                  const installed = sorted.some(e => e.event === 'INSTALLED')
                  return (
                    <div key={bomLineId} className={`bg-white border rounded-xl overflow-hidden ${installed ? 'border-green-200' : 'border-gray-200'}`}>
                      <div className={`px-5 py-3 border-b flex items-center justify-between ${installed ? 'bg-green-50' : 'bg-gray-50'}`}>
                        <div className="flex items-center gap-2">
                          <Package className="w-4 h-4 text-gray-500" />
                          <span className="font-medium text-gray-900 text-sm">{sorted[0].bomDescription}</span>
                          <span className="text-xs text-gray-400">{sorted[0].quantity} {sorted[0].unit}</span>
                        </div>
                        {installed && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">✓ Installed</span>
                        )}
                      </div>
                      <div className="px-5 py-3">
                        <div className="relative">
                          <div className="absolute left-3.5 top-4 bottom-4 w-0.5 bg-gray-100" />
                          <div className="space-y-3">
                            {sorted.map(ev => {
                              const meta = CUSTODY_EVENT_META[ev.event]
                              return (
                                <div key={ev.id} className="flex items-start gap-3">
                                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-sm flex-shrink-0 z-10 ${meta.bg}`}>
                                    {meta.icon}
                                  </div>
                                  <div className="flex-1 min-w-0 pt-0.5">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className={`text-xs font-semibold ${meta.text}`}>{meta.label}</span>
                                      <span className="text-xs text-gray-500">{ev.employeeName}</span>
                                      <span className="text-xs text-gray-400">
                                        {new Date(ev.timestamp).toLocaleString('en-AU', {day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'})}
                                      </span>
                                      {ev.signedOff && <span className="text-xs text-green-600">✓ signed off</span>}
                                    </div>
                                    {ev.notes && <p className="text-xs text-gray-400 mt-0.5">{ev.notes}</p>}
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )
          })()}
        </div>
      )}
      {(activeTab as string) === 'payment-clearance' && (() => {
        const jobSubs  = MOCK_SUBCONTRACTS.filter(sc => sc.jobId === record.jobId || sc.jobRef === record.jobNumber)
        const payRows  = jobSubs.flatMap(sc =>
          sc.milestones.map(ms => ({ ...ms, subName: sc.subcontractorName, ref: sc.reference, bcipaStatus: sc.bcipaStatus }))
        )
        const totalPayable = payRows.reduce((s, r) => s + r.amountPayable, 0)
        const totalPaid    = payRows.filter(r => r.paid).reduce((s, r) => s + r.amountPayable, 0)
        const unpaidDue    = payRows.filter(r => !r.paid && r.completedDate).length
        const allClear     = unpaidDue === 0
        const fmtAUD = (n: number) => new Intl.NumberFormat('en-AU', { style:'currency', currency:'AUD', minimumFractionDigits:0 }).format(n)
        const fmtD   = (iso?: string) => iso ? new Date(iso).toLocaleDateString('en-AU', { day:'numeric', month:'short', year:'numeric' }) : '—'
        return (
          <div className="space-y-5">
            <div className={`rounded-xl border p-4 ${allClear ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
              <div className="flex items-start gap-3">
                {allClear
                  ? <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  : <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />}
                <div className="flex-1">
                  <div className={`font-semibold text-sm ${allClear ? 'text-green-800' : 'text-red-800'}`}>
                    BIF Act 2017 (Qld) — Payment clearance: {allClear ? 'CLEARED' : 'OUTSTANDING'}
                  </div>
                  <p className={`text-xs mt-1 ${allClear ? 'text-green-700' : 'text-red-700'}`}>
                    {allClear
                      ? 'All subcontractor payments confirmed paid. The payment clearance schedule may be signed and included in the handover pack.'
                      : `${unpaidDue} subcontractor payment${unpaidDue !== 1 ? 's' : ''} outstanding. These must be settled before the handover package is finalised and the payment clearance schedule signed.`}
                  </p>
                </div>
                <button
                  onClick={() => generatePaymentClearanceSchedule(record, MOCK_SUBCONTRACTS)}
                  className="flex items-center gap-1.5 text-xs bg-[#1F3864] text-white px-3 py-1.5 rounded-lg font-medium hover:bg-[#2E75B6] flex-shrink-0"
                >
                  <Download className="w-3.5 h-3.5" />Generate clearance schedule
                </button>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="px-5 py-3.5 border-b bg-gray-50 flex items-center justify-between">
                <h3 className="font-semibold text-gray-900 text-sm">Payment register — {record.jobNumber}</h3>
                <div className="flex items-center gap-3 text-xs">
                  <span className="text-gray-500">{payRows.filter(r => r.paid).length}/{payRows.length} paid</span>
                  <span className={`font-semibold ${allClear ? 'text-green-700' : 'text-red-600'}`}>{fmtAUD(totalPaid)} / {fmtAUD(totalPayable)}</span>
                </div>
              </div>
              {payRows.length === 0 ? (
                <div className="py-14 text-center text-gray-400">
                  <Building className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">No subcontract milestones for {record.jobNumber}</p>
                  <p className="text-xs mt-1">Manage subcontracts in the Subcontracts module</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="text-xs text-gray-500 bg-gray-50 border-b">
                      <th className="text-left px-4 py-3 font-medium">Subcontractor</th>
                      <th className="text-left px-3 py-3 font-medium">Milestone</th>
                      <th className="text-left px-3 py-3 font-medium">Invoice</th>
                      <th className="text-right px-3 py-3 font-medium">Payable</th>
                      <th className="text-center px-3 py-3 font-medium">Status</th>
                      <th className="text-center px-4 py-3 font-medium">Paid date</th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-50">
                      {payRows.map((r, i) => (
                        <tr key={i} className={`${!r.paid && r.completedDate ? 'bg-red-50/40' : 'hover:bg-gray-50'}`}>
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900 text-sm">{r.subName}</div>
                            <div className="text-xs text-gray-400">{r.ref}</div>
                          </td>
                          <td className="px-3 py-3 max-w-[180px]">
                            <div className="text-xs text-gray-700 truncate" title={r.description}>{r.description}</div>
                            {r.completedDate && <div className="text-xs text-gray-400 mt-0.5">Completed {fmtD(r.completedDate)}</div>}
                          </td>
                          <td className="px-3 py-3 text-xs font-mono text-gray-600">{r.invoiceRef ?? '—'}</td>
                          <td className="px-3 py-3 text-right tabular-nums font-semibold text-sm">{fmtAUD(r.amountPayable)}</td>
                          <td className="px-3 py-3 text-center">
                            {r.paid ? (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium flex items-center gap-1 justify-center w-fit mx-auto">
                                <CheckCircle className="w-3 h-3" />Paid
                              </span>
                            ) : r.completedDate ? (
                              <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-bold flex items-center gap-1 justify-center w-fit mx-auto">
                                <AlertTriangle className="w-3 h-3" />Unpaid
                              </span>
                            ) : (
                              <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Not due</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center text-xs text-gray-500">{r.paidAt ? fmtD(r.paidAt) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="border-t-2 border-gray-200 bg-gray-50">
                      <tr>
                        <td colSpan={3} className="px-4 py-3 text-sm font-semibold text-gray-700">Total</td>
                        <td className="px-3 py-3 text-right tabular-nums font-bold text-gray-900">{fmtAUD(totalPayable)}</td>
                        <td colSpan={2} className="px-3 py-3 text-center text-xs font-semibold text-green-700">{fmtAUD(totalPaid)} confirmed paid</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-xs text-blue-800">
              <strong>BIF Act 2017 (Qld) — Handover requirement:</strong> At practical completion, the head contractor must provide a final payment clearance schedule to the principal confirming all subcontractors and suppliers have been paid in full. This document, signed by the PM and acknowledged by the client, forms part of the handover pack and is required before retention release can be claimed.
            </div>
          </div>
        )
      })()}
    </div>
  )
}

// Main page


// ─────────────────────────────────────────────────────────────────
// PHASE 8 — HANDOVER PACK PDF
// Comprehensive handover document combining the full document
// checklist, DLP details, contract reference, and warranty summary.
// ─────────────────────────────────────────────────────────────────
function generateHandoverPackPDF(record: any, contract: any) {
  const now = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
  const complete = record.documents.filter((d: any) => d.status === 'COMPLETE').length
  const total = record.documents.filter((d: any) => d.required).length

  const docRows = record.documents.map((d: any, i: number) => `
    <tr style="background:${i%2===0?'#fff':'#f9fafb'}">
      <td style="padding:6px 10px;font-size:9.5pt">${d.name}${!d.required?' <span style="color:#9ca3af;font-size:8.5pt">(optional)</span>':''}</td>
      <td style="padding:6px 8px;text-align:center;font-weight:bold;color:${d.status==='COMPLETE'?'#166534':d.status==='IN_PROGRESS'?'#1d4ed8':'#6b7280'}">${d.status.replace(/_/g,' ')}</td>
      <td style="padding:6px 8px;text-align:center;font-size:9pt">${d.dueDate ? new Date(d.dueDate).toLocaleDateString('en-AU') : '—'}</td>
      <td style="padding:6px 10px;font-size:9pt;color:#666;font-style:italic">${d.notes ?? ''}</td>
    </tr>`).join('')

  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>Handover Pack — ${record.jobNumber}</title>
<style>
*{box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:10.5pt;color:#111;margin:18mm}
h1{font-size:22pt;font-weight:900;color:#1B3A6B;margin:0}
h2{font-size:12pt;color:#1B3A6B;border-bottom:2px solid #1B3A6B;padding-bottom:4px;margin:18px 0 10px}
.hdr{border-bottom:3px solid #1B3A6B;padding-bottom:10px;margin-bottom:16px;display:flex;justify-content:space-between;align-items:flex-end}
.cert-banner{background:#1B3A6B;color:#fff;border-radius:6px;padding:14px 20px;margin:14px 0;text-align:center}
.info table{width:100%;border-collapse:collapse;margin-bottom:12px}
.info td{padding:5px 8px;border-bottom:1px solid #f3f4f6}.info td:first-child{width:36%;font-weight:bold;color:#555;background:#f9fafb}
table.docs{width:100%;border-collapse:collapse;font-size:9.5pt;margin-bottom:14px}
th{background:#1B3A6B;color:#fff;padding:6px 10px;text-align:left}
.kpi{display:flex;gap:10px;margin-bottom:12px}
.kpi-box{flex:1;border:1px solid #e5e7eb;border-radius:5px;padding:8px;text-align:center}
.kpi-v{font-size:18pt;font-weight:900}
.sig-row{display:flex;gap:30px;margin-top:18px}
.sig-block{flex:1}.sig-line{border-bottom:1.5px solid #333;height:32px;margin-bottom:4px}.sig-lbl{font-size:8pt;color:#888}
.footer{font-size:7.5pt;color:#aaa;text-align:center;margin-top:18px;border-top:1px solid #eee;padding-top:7px}
@media print{body{margin:14mm}}
</style></head><body>
<div class="hdr">
  <div><h1>MCB Civil Electrical</h1><div style="font-size:9pt;color:#888">ABN 44 128 993 087 · 14 Commerce Dr, Salisbury QLD 4107 · 07 3344 5500</div></div>
  <div style="text-align:right"><div style="font-size:9pt;color:#888">Date: ${now}</div></div>
</div>

<div class="cert-banner">
  <div style="font-size:11pt;letter-spacing:2px;text-transform:uppercase;opacity:.85">Project Handover Pack</div>
  <div style="font-size:22pt;font-weight:900;margin-top:4px">${record.jobNumber}</div>
  <div style="font-size:11pt;margin-top:4px">${record.description}</div>
</div>

<div class="info"><table>
  <tr><td>Client</td><td><strong>${record.clientName}</strong></td></tr>
  <tr><td>Target handover date</td><td>${record.targetHandoverDate ? new Date(record.targetHandoverDate).toLocaleDateString('en-AU', {day:'numeric',month:'long',year:'numeric'}) : 'TBC'}</td></tr>
  <tr><td>Actual handover date</td><td>${record.actualHandoverDate ? new Date(record.actualHandoverDate).toLocaleDateString('en-AU', {day:'numeric',month:'long',year:'numeric'}) : 'Pending'}</td></tr>
  <tr><td>Contract reference</td><td>${contract?.contractNumber ?? '—'}</td></tr>
  <tr><td>Defects liability period</td><td>${record.dlpMonths} months from handover</td></tr>
  <tr><td>Retention held</td><td>${contract ? '$' + contract.retentionHeld.toLocaleString() : '—'}</td></tr>
</table></div>

<div class="kpi">
  <div class="kpi-box"><div class="kpi-v" style="color:${complete===total?'#166534':'#d97706'}">${complete}/${total}</div><div style="font-size:9pt;color:#6b7280">Required docs complete</div></div>
  <div class="kpi-box"><div class="kpi-v" style="color:#1B3A6B">${record.percentageComplete}%</div><div style="font-size:9pt;color:#6b7280">Overall readiness</div></div>
  <div class="kpi-box"><div class="kpi-v">${record.dlpMonths}mo</div><div style="font-size:9pt;color:#6b7280">DLP period</div></div>
</div>

<h2>Handover document checklist</h2>
<table class="docs">
  <thead><tr><th>Document</th><th style="text-align:center">Status</th><th style="text-align:center">Due date</th><th>Notes / reference</th></tr></thead>
  <tbody>${docRows}</tbody>
</table>

<h2>Defects liability period</h2>
<div class="info"><table>
  <tr><td>DLP duration</td><td>${record.dlpMonths} months</td></tr>
  <tr><td>Commences</td><td>On practical completion / actual handover date</td></tr>
  <tr><td>Retention release</td><td>50% on practical completion, 50% on DLP expiry</td></tr>
</table></div>
<p style="font-size:9.5pt;color:#92400e;background:#fef3c7;border:1px solid #fbbf24;border-radius:4px;padding:8px 14px">⚠ A retention release claim must be submitted to the superintendent at the end of the DLP. Do not wait for automatic release.</p>

<h2>Chain of custody declaration</h2>
<p style="font-size:10pt;color:#555">MCB Civil Electrical Pty Ltd confirms that all build materials, equipment, and installations described in this handover pack have been supplied, installed, and tested in accordance with the contract specification and applicable Australian Standards / TMR MRTS / Energex technical specifications referenced in the project documentation.</p>

<div class="sig-row">
  <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">Project Manager — MCB Civil Electrical · name &amp; date</div></div>
  <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">Client representative — handover accepted · name &amp; date</div></div>
</div>

<div class="footer">MCB Civil Electrical Pty Ltd · Handover Pack · ${record.jobNumber} · Generated ${now} · Retain for project and warranty records</div>
</body></html>`
  const w = window.open('', '_blank', 'width=1020,height=820')
  if (!w) { alert('Pop-up blocked'); return }
  w.document.write(html); w.document.close()
  setTimeout(() => { w.focus(); w.print() }, 350)
}


// ─────────────────────────────────────────────────────────────────
// PHASE 8 — FORM 16 (Electrical Installation Certificate, QLD)
// Statutory certificate required before energising any new
// electrical installation. Format per Electrical Safety Regulation.
// ─────────────────────────────────────────────────────────────────
function generateForm16(record: any) {
  const now = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
  const f16doc = record.documents.find((d: any) => d.name.toLowerCase().includes('form 16'))

  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>Form 16 — ${record.jobNumber}</title>
<style>
*{box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:10.5pt;color:#111;margin:18mm}
.qg-hdr{background:#7b2d8e;color:#fff;padding:14px 20px;border-radius:6px 6px 0 0;display:flex;justify-content:space-between;align-items:center}
.qg-hdr h1{font-size:15pt;font-weight:900;margin:0}
.form-code{font-family:monospace;font-size:13pt;font-weight:bold;background:rgba(255,255,255,.18);padding:4px 14px;border-radius:4px}
.body-box{border:2px solid #7b2d8e;border-top:none;border-radius:0 0 6px 6px;padding:18px 20px}
h2{font-size:10.5pt;color:#7b2d8e;border-bottom:1.5px solid #7b2d8e;padding-bottom:3px;margin:14px 0 8px}
.info table{width:100%;border-collapse:collapse;margin-bottom:10px}
.info td{padding:4px 8px;border-bottom:1px solid #f3f4f6}.info td:first-child{width:38%;font-weight:bold;color:#555;background:#f9fafb}
.decl-box{background:#faf5ff;border:1px solid #d8b4fe;border-radius:4px;padding:10px 14px;margin:14px 0;font-size:9.5pt;line-height:1.7}
.sig-row{display:flex;gap:30px;margin-top:18px}
.sig-block{flex:1}.sig-line{border-bottom:1.5px solid #333;height:32px;margin:14px 0 4px}.sig-lbl{font-size:8pt;color:#888}
.footer{font-size:7.5pt;color:#aaa;text-align:center;margin-top:18px;border-top:1px solid #eee;padding-top:7px}
@media print{body{margin:14mm}}
</style></head><body>

<div class="qg-hdr">
  <h1>Electrical Safety Office — Queensland Government<br><span style="font-size:11pt;font-weight:400">Electrical Work — Notice of Completion</span></h1>
  <div class="form-code">FORM 16</div>
</div>
<div class="body-box">

<div class="info"><table>
  <tr><td>Job / project reference</td><td><strong>${record.jobNumber}</strong></td></tr>
  <tr><td>Description of works</td><td>${record.description}</td></tr>
  <tr><td>Client</td><td>${record.clientName}</td></tr>
  <tr><td>Form 16 reference</td><td>${f16doc?.notes?.split(' ')[0] ?? `F16-${new Date().getFullYear()}-PENDING`}</td></tr>
  <tr><td>Date of completion</td><td>${now}</td></tr>
</table></div>

<h2>Electrical contractor details</h2>
<div class="info"><table>
  <tr><td>Contractor name</td><td>MCB Civil Electrical Pty Ltd</td></tr>
  <tr><td>Contractor licence number</td><td>QBCC 1234567</td></tr>
  <tr><td>ABN</td><td>44 128 993 087</td></tr>
  <tr><td>Address</td><td>14 Commerce Dr, Salisbury QLD 4107</td></tr>
</table></div>

<h2>Licensed worker details</h2>
<div class="info"><table>
  <tr><td>Licensed electrical worker</td><td>${f16doc?.notes?.includes('signed by') ? f16doc.notes.split('signed by ')[1] : '_________________________'}</td></tr>
  <tr><td>Licence number</td><td>_________________________</td></tr>
  <tr><td>Licence class</td><td>Electrician (unrestricted) — EL2</td></tr>
</table></div>

<div class="decl-box">
  <strong>Declaration:</strong> I declare that the electrical installation work described in this notice has been carried out in accordance with the Electrical Safety Act 2002 (Qld), the Electrical Safety Regulation 2013 (Qld), AS/NZS 3000 Wiring Rules, and any other applicable standards. The installation has been tested and is electrically safe.
</div>

<h2>Test results summary</h2>
<div class="info"><table>
  <tr><td>Insulation resistance test</td><td>PASS — refer to attached test certificate</td></tr>
  <tr><td>Earth continuity test</td><td>PASS</td></tr>
  <tr><td>Polarity verification</td><td>PASS</td></tr>
  <tr><td>RCD operation (if fitted)</td><td>PASS — trip time within AS/NZS 3760 limits</td></tr>
</table></div>

<div class="sig-row">
  <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">Licensed electrical worker — name, licence number &amp; signature</div></div>
  <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">Date</div></div>
</div>

</div>
<div class="footer">Electrical Safety Office, Queensland Government · Form 16 · ${record.jobNumber} · Generated ${now}<br>This notice must be retained with the electrical installation records for a minimum of 5 years.</div>
</body></html>`
  const w = window.open('', '_blank', 'width=960,height=820')
  if (!w) { alert('Pop-up blocked'); return }
  w.document.write(html); w.document.close()
  setTimeout(() => { w.focus(); w.print() }, 350)
}


// ─────────────────────────────────────────────────────────────────
// PHASE 8 — PRACTICAL COMPLETION CERTIFICATE
// Formal certificate confirming the works have reached practical
// completion under the contract conditions.
// ─────────────────────────────────────────────────────────────────
function generatePracticalCompletionCertificate(record: any, contract: any) {
  const now = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
  const pcDate = record.actualHandoverDate ?? record.targetHandoverDate

  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>Practical Completion Certificate — ${record.jobNumber}</title>
<style>
*{box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:11pt;color:#111;margin:24mm}
.cert{border:6px double #1B3A6B;border-radius:8px;padding:30px 36px;text-align:center}
h1{font-size:22pt;font-weight:900;color:#1B3A6B;margin:0 0 4px;text-transform:uppercase;letter-spacing:2px}
.sub{font-size:10pt;color:#888;margin-bottom:24px}
.proj-name{font-size:18pt;font-weight:900;color:#1B3A6B;margin:18px 0;padding:14px 20px;border-top:2px solid #e5e7eb;border-bottom:2px solid #e5e7eb}
.info-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin:20px 0;text-align:left}
.info-box{border:1px solid #e5e7eb;border-radius:6px;padding:10px 14px}
.info-lbl{font-size:9pt;color:#888;margin-bottom:3px}
.info-val{font-size:11pt;font-weight:bold;color:#1B3A6B}
.pc-date{font-size:28pt;font-weight:900;color:#1B3A6B;margin:16px 0;padding:14px;background:#e8f0fe;border-radius:8px}
.statement{font-size:10.5pt;color:#444;line-height:1.8;margin:20px 0;text-align:left;padding:0 20px}
.sig-row{display:flex;gap:40px;margin-top:30px;text-align:left}
.sig-block{flex:1}.sig-line{border-bottom:1.5px solid #333;height:32px;margin-bottom:4px}
.sig-lbl{font-size:8pt;color:#888}
.seal{display:inline-block;border:3px solid #1B3A6B;border-radius:50%;padding:12px 16px;color:#1B3A6B;font-weight:900;font-size:10pt;margin-top:16px;transform:rotate(-6deg)}
.footer{font-size:8pt;color:#aaa;margin-top:20px;border-top:1px solid #eee;padding-top:10px}
@media print{body{margin:18mm}}
</style></head><body>
<div class="cert">
  <h1>Certificate of Practical Completion</h1>
  <div class="sub">MCB Civil Electrical Pty Ltd · ABN 44 128 993 087</div>

  <p style="font-size:10.5pt;color:#555">This certifies that the works described below have reached Practical Completion under the contract conditions</p>

  <div class="proj-name">${record.jobNumber}<br><span style="font-size:13pt;font-weight:400">${record.description}</span></div>

  <div class="info-grid">
    <div class="info-box"><div class="info-lbl">Client</div><div class="info-val">${record.clientName}</div></div>
    <div class="info-box"><div class="info-lbl">Contract reference</div><div class="info-val">${contract?.contractNumber ?? '—'}</div></div>
    <div class="info-box"><div class="info-lbl">Contract type</div><div class="info-val">${contract?.contractType ?? '—'}</div></div>
    <div class="info-box"><div class="info-lbl">Defects liability period</div><div class="info-val">${record.dlpMonths} months</div></div>
  </div>

  <div style="font-size:10pt;color:#555;font-weight:bold;margin-top:4px">DATE OF PRACTICAL COMPLETION</div>
  <div class="pc-date">${pcDate ? new Date(pcDate).toLocaleDateString('en-AU', {day:'numeric',month:'long',year:'numeric'}) : 'TBC'}</div>

  <div class="statement">
    The Contractor certifies that the works under the above contract have reached the stage of Practical Completion, being the stage at which the works are complete except for minor omissions and defects which do not prevent the works from being reasonably capable of being used for their intended purpose. The Defects Liability Period of ${record.dlpMonths} months commences from the date stated above. Retention release will be processed in accordance with the contract conditions: 50% upon Practical Completion and the remaining 50% upon expiry of the Defects Liability Period, subject to satisfactory rectification of any defects identified.
  </div>

  <div class="sig-row">
    <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">Project Manager — MCB Civil Electrical Pty Ltd · name &amp; date</div></div>
    <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">Superintendent / Client representative — certified &amp; date</div></div>
  </div>

  <div class="seal">CERTIFIED</div>
  <div class="footer">MCB Civil Electrical Pty Ltd · Certificate of Practical Completion · ${record.jobNumber} · Generated ${now}<br>This certificate forms part of the permanent contract record and triggers the Defects Liability Period.</div>
</div>
</body></html>`
  const w = window.open('', '_blank', 'width=880,height=820')
  if (!w) { alert('Pop-up blocked'); return }
  w.document.write(html); w.document.close()
  setTimeout(() => { w.focus(); w.print() }, 350)
}
export function Handover() {
  const [records]           = useState(MOCK_HANDOVER_RECORDS)
  const [selected, setSelected] = useState<HandoverRecord | null>(null)

  // Live handover packs from Neon — via handoverService
  const { data: livePacks = [] } = useQuery({
    queryKey: ['handover-packs'],
    queryFn:  () => fetchHandoverPacks(),
    staleTime: 60_000,
  })
  const livePackCount = livePacks.length
  const livePackByJob = new Map(livePacks.map(p => [p.jobId, p]))

  if (selected) return <HandoverDetail record={selected} onBack={() => setSelected(null)} />

  const overallPct = Math.round(records.reduce((s, r) => s + r.percentageComplete, 0) / records.length)

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Handover &amp; Certificates</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          {records.length} jobs tracked · {overallPct}% average readiness
          {livePackCount > 0 && <span className="ml-2 text-blue-600 font-medium">· {livePackCount} live from Neon</span>}
        </p>
      </div>

      {/* Summary row */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Ready for handover',  value: records.filter(r => r.percentageComplete === 100).length, bg: 'bg-green-50', text: 'text-green-700' },
          { label: 'In progress',         value: records.filter(r => r.status === 'IN_PROGRESS').length,    bg: 'bg-blue-50',  text: 'text-blue-700' },
          { label: 'Not yet started',     value: records.filter(r => r.status === 'NOT_STARTED').length,    bg: 'bg-gray-50',  text: 'text-gray-600' },
        ].map(c => (
          <div key={c.label} className={`rounded-xl border border-gray-200 p-4 ${c.bg}`}>
            <div className={`text-3xl font-bold ${c.text}`}>{c.value}</div>
            <div className="text-xs text-gray-500 mt-1">{c.label}</div>
          </div>
        ))}
      </div>

      {/* Handover cards */}
      <div className="space-y-4">
        {records.map(r => {
          const complete = r.documents.filter(d => d.status === 'COMPLETE').length
          const reqTotal = r.documents.filter(d => d.required).length
          const pct      = r.percentageComplete
          const hs       = HO_STATUS[r.status] ?? HO_STATUS.NOT_STARTED
          const daysLeft = r.targetHandoverDate
            ? Math.ceil((new Date(r.targetHandoverDate).getTime() - new Date('2026-06-17').getTime()) / 86400000)
            : null
          return (
            <div key={r.id} onClick={() => setSelected(r)}
              className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-gray-300 cursor-pointer group transition-colors">
              <div className="px-5 py-4 flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-bold text-gray-900">{r.jobNumber}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${hs.bg} ${hs.text}`}>{hs.label}</span>
                    {daysLeft !== null && daysLeft <= 30 && (
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${daysLeft <= 14 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                        {daysLeft > 0 ? `${daysLeft}d` : 'Overdue'}
                      </span>
                    )}
                    {livePackByJob.get(r.jobId)?.itpComplete && (
                      <span className="text-xs px-1.5 py-0.5 rounded-full font-medium bg-teal-100 text-teal-700">
                        ✓ ITP complete
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-700 font-medium">{r.description}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{r.clientName} · {reqTotal} required documents · {complete} complete · {r.dlpMonths}mo DLP</p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className={`text-xl font-bold ${pct === 100 ? 'text-green-600' : 'text-brand-blue'}`}>{pct}%</div>
                    <div className="text-xs text-gray-400">ready</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 flex-shrink-0" />
                </div>
              </div>
              <div className="h-1.5 bg-gray-100">
                <div className={`h-full transition-all ${pct === 100 ? 'bg-green-500' : 'bg-brand-blue'}`} style={{ width: `${pct}%` }} />
              </div>
            </div>
          )
        })}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800">
        <p className="font-semibold mb-1">📄 Handover certificate</p>
        <p>When all required documents are marked complete, the system can generate a Handover Certificate PDF that includes a summary of works, Form 16 references, warranty details, and the chain of custody for all build materials. Click into any job and click <strong>Generate handover pack</strong>.</p>
      </div>
    </div>
  )
}
